import { logoPrincipal } from '../assets';
import { useEffect, useRef, useState } from 'react';

export default function AboutAgency() {
  const [inView, setInView] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold: 0.18 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  // ✅ Más rápido: antes 500ms base + escalonado largo
  const baseDelay = 120; // 👈 baja de 500 a 120
  const ease = 'ease-[cubic-bezier(0.16,1,0.3,1)]';

  return (
    <section ref={sectionRef} id="about" className="relative py-32 overflow-hidden bg-[#EEF2F7] dark:bg-slate-950">
      {/* Background modern */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-32 w-[560px] h-[560px] bg-lime-400/10 blur-[150px] rounded-full" />
        <div className="absolute -bottom-40 -right-24 w-[720px] h-[720px] bg-[#3662E2]/12 blur-[170px] rounded-full" />
        <div className="absolute inset-0 opacity-[0.06] dark:opacity-[0.05] bg-[radial-gradient(#3662E2_1px,transparent_1px)] [background-size:28px_28px] [mask-image:linear-gradient(to_bottom,transparent,black_18%,black_82%,transparent)]" />
        <div className="absolute inset-0 opacity-[0.035] dark:opacity-[0.03] mix-blend-multiply dark:mix-blend-soft-light bg-[url('https://www.transparenttextures.com/patterns/asfalt-light.png')]" />

        {/* ✅ logoPrincipal como watermark en TODA la sección */}
        <img
          src={logoPrincipal}
          alt=""
          className="absolute inset-0 w-full h-full object-contain opacity-[0.06] dark:opacity-[0.10]"
          style={{
            transform: 'scale(1.22)',
            filter: 'grayscale(1) contrast(1.05)',
          }}
        />
        {/* velo sutil para que el watermark no “ensucie” */}
        <div className="absolute inset-0 bg-white/10 dark:bg-black/20" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="flex flex-col items-center text-center gap-6 mb-16">
          <div className="text-center">
            <h2
              className={[
                // ✅ FIX: en modo claro ahora es oscuro; en oscuro sigue blanco
                'text-5xl md:text-7xl font-display font-black text-secondary dark:text-white tracking-tight leading-none',
                'transform-gpu transition-all duration-[850ms]',
                ease,
                inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4',
              ].join(' ')}
              style={{ transitionDelay: `${baseDelay + 80}ms` }}
            >
              Sobre <br />
              <span className="text-secondary dark:text-white">Next Global Media</span>
            </h2>
          </div>

          <div className="text-center">
            <p
              className={[
                'w-full text-slate-600 dark:text-slate-300 text-lg font-light leading-relaxed',
                'transform-gpu transition-all duration-[850ms]',
                ease,
                inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4',
              ].join(' ')}
              style={{ transitionDelay: `${baseDelay + 160}ms` }}
            >
              No trabajamos a ciegas, trabajamos con estrategia. Tomamos lo que está funcionando para que tu negocio
              crezca de verdad. Nos guiamos por números reales y resultados medibles. Analizamos la información en
              tiempo real para ayudarte a llegar a las personas correctas, fortalecer tu presencia digital y competir al
              mismo nivel que las grandes marcas, pero con estrategias inteligentes y bien ejecutadas.
            </p>
          </div>
        </div>

        {/* Cards row (Misión / Visión) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div
            className={[
              'lg:col-span-6 rounded-[2rem] p-10 bento-card relative overflow-hidden',
              'border border-white/40 dark:border-white/10',
              'bg-white/55 dark:bg-slate-950/35 backdrop-blur-xl',
              'shadow-[0_20px_70px_rgba(2,6,23,0.10)] dark:shadow-[0_25px_90px_rgba(0,0,0,0.45)]',
              'transform-gpu transition-all duration-[800ms]',
              ease,
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6',
            ].join(' ')}
            style={{ transitionDelay: `${baseDelay + 240}ms` }}
          >
            <div className="absolute -top-24 -right-24 w-80 h-80 bg-lime-400/10 blur-[110px] rounded-full" />
            <div className="flex items-start justify-between gap-6 relative z-10">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-lime-400/15 border border-lime-400/25 text-slate-900 dark:text-slate-100 text-[11px] font-black tracking-[0.3em] uppercase">
                  Misión
                </div>
                <h3 className="mt-6 text-3xl md:text-4xl font-display font-black text-secondary dark:text-slate-100 tracking-tight">
                  Construimos marcas fuertes que sobresalen en su mercado. Cada paso que damos busca que tu negocio
                  gane presencia, conecte con más personas y aumente sus ventas.
                </h3>
              </div>

              <div className="w-14 h-14 rounded-2xl bg-lime-400/20 border border-lime-400/25 flex items-center justify-center">
                <span className="material-symbols-outlined text-slate-900 dark:text-slate-100 text-[28px]">
                  rocket_launch
                </span>
              </div>
            </div>
          </div>

          <div
            className={[
              'lg:col-span-6 rounded-[2rem] p-10 bento-card relative overflow-hidden',
              'border border-white/40 dark:border-white/10',
              'bg-white/55 dark:bg-slate-950/35 backdrop-blur-xl',
              'shadow-[0_20px_70px_rgba(2,6,23,0.10)] dark:shadow-[0_25px_90px_rgba(0,0,0,0.45)]',
              'transform-gpu transition-all duration-[800ms]',
              ease,
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6',
            ].join(' ')}
            style={{ transitionDelay: `${baseDelay + 320}ms` }}
          >
            <div className="absolute -bottom-28 -left-24 w-96 h-96 bg-[#3662E2]/12 blur-[120px] rounded-full" />
            <div className="flex items-start justify-between gap-6 relative z-10">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#3662E2]/15 border border-[#3662E2]/25 text-slate-900 dark:text-slate-100 text-[11px] font-black tracking-[0.3em] uppercase">
                  Visión
                </div>
                <h3 className="mt-6 text-3xl md:text-4xl font-display font-black text-secondary dark:text-slate-100 tracking-tight">
                  Ser una empresa que transforma negocios en marcas sólidas y reconocidas a nivel internacional.
                  Creemos en el crecimiento grande, en las ideas que rompen barreras y en usar la tecnología como
                  herramienta para crear oportunidades reales.
                </h3>
              </div>

              <div className="w-14 h-14 rounded-2xl bg-[#3662E2]/20 border border-[#3662E2]/25 flex items-center justify-center">
                <span className="material-symbols-outlined text-slate-900 dark:text-slate-100 text-[28px]">
                  public
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Valores */}
        <div className="mt-10">
          <div
            className={[
              'flex items-center justify-between gap-6 mb-6',
              'transform-gpu transition-all duration-[700ms]',
              ease,
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
            ].join(' ')}
            style={{ transitionDelay: `${baseDelay + 380}ms` }}
          >
            <h3 className="text-2xl md:text-3xl font-display font-black text-secondary dark:text-slate-100 tracking-tight">
              Valores
            </h3>

            <div className="hidden md:flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 font-bold tracking-[0.25em] uppercase">
              Cultura <span className="w-10 h-[1px] bg-slate-300/60 dark:bg-slate-700/70" /> Ejecución
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-5">
            {[
              { title: 'Innovación', icon: 'neurology', accent: 'bg-lime-400/14 border-lime-400/25' },
              { title: 'Excelencia', icon: 'verified', accent: 'bg-[#3662E2]/14 border-[#3662E2]/25' },
              { title: 'Estrategia', icon: 'strategy', accent: 'bg-cyan-400/10 border-cyan-400/20' },
              { title: 'Compromiso', icon: 'handshake', accent: 'bg-emerald-400/10 border-emerald-400/20' },
              { title: 'Resultados', icon: 'query_stats', accent: 'bg-violet-400/10 border-violet-400/20' },
            ].map((v, idx) => (
              <div
                key={v.title}
                className={[
                  'group rounded-2xl p-6 bento-card relative overflow-hidden',
                  'border border-white/35 dark:border-white/10',
                  'bg-white/55 dark:bg-slate-950/35 backdrop-blur-xl',
                  'shadow-[0_18px_55px_rgba(2,6,23,0.09)] dark:shadow-[0_22px_70px_rgba(0,0,0,0.45)]',
                  'transform-gpu transition-all duration-[750ms]',
                  ease,
                  inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6',
                ].join(' ')}
                style={{ transitionDelay: `${baseDelay + 420 + idx * 70}ms` }}
              >
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                  <div className="absolute -top-24 -right-24 w-64 h-64 bg-white/40 dark:bg-white/10 blur-[90px] rounded-full" />
                </div>

                <div
                  className={[
                    'w-14 h-14 rounded-2xl flex items-center justify-center border',
                    v.accent,
                    'transition-transform duration-300 group-hover:scale-[1.03]',
                  ].join(' ')}
                >
                  <span className="material-symbols-outlined text-[28px] text-secondary dark:text-slate-100">
                    {v.icon}
                  </span>
                </div>

                <div className="mt-6">
                  <div className="text-lg font-black text-secondary dark:text-slate-100 tracking-tight">{v.title}</div>
                  <div className="mt-2 h-[2px] w-0 bg-lime-400 group-hover:w-10 transition-all duration-500" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}